import Layout from '../../components/Layout';
import GuideCard from '../../components/GuideCard';
import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Sparkles, Heart, Scale } from 'lucide-react';

const makeupGuides = [
  {
    title: "Everyday Makeup Looks",
    subtitle: "Simple, flattering looks you can wear daily to work, school, or errands.",
    category: "Looks",
    link: "/makeup/everyday",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/ccimqd5h_everyday-makeup.jpg.png"
  },
  {
    title: "Minimal Makeup Guide",
    subtitle: "The 'no-makeup makeup' look—enhancing your features with just a few products.",
    category: "Style",
    link: "/makeup/minimal",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/oqoaxfdo_minimal-makeup.jpg.png"
  },
  {
    title: "Makeup Essentials",
    subtitle: "The core products every makeup bag should have, regardless of skill level.",
    category: "Essentials",
    link: "/makeup/essentials",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/fkh19m3r_makeup-essentials.jpg.png"
  }
];

const browseTopics = [
  { icon: Sparkles, label: "Everyday Looks", link: "/makeup/everyday" },
  { icon: Heart, label: "Minimal Makeup", link: "/makeup/minimal" },
  { icon: Palette, label: "Essentials", link: "/makeup/essentials" },
  { icon: Scale, label: "Compare Products", link: "/makeup/compare" },
];

const MakeupLanding = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="makeup-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
              Makeup
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1C1C1C] leading-tight mb-6">
              Effortless looks, <span className="italic font-normal">curated products</span>
            </h1>
            <p className="text-base md:text-lg text-[#374151] leading-relaxed">
              Whether you prefer a full face or barely-there glow, our makeup guides help you choose quality products and create looks that enhance your natural features.
            </p>
          </div>
        </div>
      </section>

      {/* Start Here Section */}
      <section data-testid="makeup-start-here" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="p-8 md:p-12 bg-[#F5F5F0] border border-[#E5E5E5]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <span className="text-xs tracking-widest uppercase font-medium text-[#3A4D39] mb-2 block">New to Makeup?</span>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
                  Start with the essentials
                </h3>
                <p className="text-[#374151] max-w-xl">
                  Our essentials guide covers the core products you need to build a versatile makeup collection, no matter your style.
                </p>
              </div>
              <Link 
                to="/makeup/essentials"
                data-testid="makeup-start-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29] flex-shrink-0"
              >
                View Essentials
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Topic */}
      <section data-testid="makeup-browse-topics" className="py-16 md:py-20 bg-[#FDFCF8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Browse by Topic
          </h2>
          <div className="flex flex-wrap gap-3">
            {browseTopics.map((topic) => (
              <Link
                key={topic.label}
                to={topic.link}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E5E5] text-[#374151] text-sm font-medium transition-all duration-300 hover:border-[#3A4D39] hover:text-[#3A4D39]"
              >
                <topic.icon className="w-4 h-4" />
                {topic.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* All Guides */}
      <section data-testid="makeup-all-guides" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C] mb-4">
              All Makeup Guides
            </h2>
            <p className="text-[#374151]">
              Explore all our makeup guides, from daily essentials to specific looks.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {makeupGuides.map((guide) => (
              <GuideCard key={guide.title} {...guide} />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MakeupLanding;
